import { createContext, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const CartContext = createContext(null);
const KEY = "atelier_cart";

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  // Refresh saved cart prices from the server (base prices are CHF and may change).
  useEffect(() => {
    if (!items.length) return;
    Promise.all(items.map((i) => api.get(`/products/${i.product_id}`).then((r) => r.data).catch(() => null)))
      .then((fresh) => setItems((prev) => prev.map((i) => {
        const p = fresh.find((f) => f && f.id === i.product_id);
        return p ? { ...i, price: p.price } : i;
      })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // One-of-one: every piece is unique, quantity is always 1 and cannot be added twice.
  const addItem = (product) => {
    let added = true;
    setItems((prev) => {
      if (prev.find((i) => i.product_id === product.id)) {
        added = false;
        return prev;
      }
      return [
        ...prev,
        {
          product_id: product.id,
          name: product.name,
          price: product.price,
          image: product.images?.[0] || "",
          size: product.size || "",
          quantity: 1,
        },
      ];
    });
    setOpen(true);
    return added;
  };

  const removeItem = (product_id) =>
    setItems((prev) => prev.filter((i) => i.product_id !== product_id));

  const clear = () => setItems([]);

  const count = items.length;
  const subtotal = items.reduce((s, i) => s + i.price, 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, clear, count, subtotal, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
